import type {ILine, IParseNode, IParsedFindResult, ISectionFind} from './parser-types'
import {NodeType} from './parser-types'
import {Parser, getText, getType, _ansiEscapeCodeRegex} from './parser'
import type {TemplateResult} from 'lit-html'
import {html} from 'lit-html'
import {ifDefined} from 'lit-html/directives/if-defined'

interface ILogLine extends IParseNode {
  text?: string
}

interface LogLineTemplateData {
  className: string
  lineNumber: number
  lineContent: TemplateResult
  timeStamp?: string
  lineUrl: string
  isGroup: boolean
  inGroup: boolean
  isError: boolean
  isWarning: boolean
  isNotice: boolean
  groupExpanded: boolean
}

const MAX_LINE_MATCHES_TO_PARSE = 100

const logLineContentTemplate = (data: LogLineTemplateData) => {
  let content
  if (data.isError) {
    content = html`<span class="CheckStep-error-text">Error: </span>${data.lineContent}`
  } else if (data.isWarning) {
    content = html`<span class="CheckStep-warning-text">Warning: </span>${data.lineContent}`
  } else if (data.isNotice) {
    content = html`<span class="CheckStep-notice-text">Notice: </span>${data.lineContent}`
  } else {
    content = data.lineContent
  }

  if (data.isGroup) {
    // prettier-ignore
    return html`<details class="js-checks-log-group" open="${ifDefined(data.groupExpanded ? 'open' : undefined)}"
      ><summary>${content}</summary></details
    >`
  }
  if (data.inGroup) {
    const spacer = '  '
    return html`${spacer}${content}`
  }
  return content
}

const stepLogLineTemplate = (data: LogLineTemplateData) => html`
  <div class="js-check-step-line CheckStep-line d-flex ${data.className}">
    <a class="CheckStep-line-number d-inline-block text-mono text-normal flex-shrink-0" href="${data.lineUrl}"
      >${data.lineNumber}</a
    >
    ${data.timeStamp &&
    html`<span class="CheckStep-line-timestamp text-mono text-normal ml-3 flex-shrink-0">${data.timeStamp}</span>`}
    <span class="CheckStep-line-content d-inline-block flex-auto ml-3 js-check-line-content"
      >${logLineContentTemplate(data)}</span
    >
  </div>
`

const checkRunLogLineTemplate = (data: LogLineTemplateData) => html`
  <div class="CheckRun-line d-flex ml-3 ${data.className}">
    ${data.timeStamp &&
    html`<span class="CheckRun-line-timestamp text-mono text-normal ml-3 flex-shrink-0">${data.timeStamp}</span>`}
    <span class="d-inline-block flex-auto ml-3 js-check-line-content">${logLineContentTemplate(data)}</span>
  </div>
`

const pipelinesLogParser = new Parser()

export class LogParser {
  logContent: string
  rawLogData: string
  lineMetaData: ILine[]
  findResult?: IParsedFindResult
  logLines: ILogLine[]
  logLineNumbers: number[]
  timestamps: string[]
  permalinkPrefix: string

  constructor(rawLogData: string, permalinkPrefix: string) {
    this.rawLogData = rawLogData
    this.permalinkPrefix = permalinkPrefix
    this.logContent = ''
    this.lineMetaData = []
    this.logLines = []
    this.logLineNumbers = []
    this.timestamps = []
  }

  updateLineMetaData() {
    const lines = this.rawLogData.split(/\r?\n/)
    // Example log line with timestamp:
    // 2019-07-11T16:56:45.9315998Z #[debug]Evaluating condition for step: 'fourth step'
    const timestampRegex = /^(.{27}Z) /

    this.timestamps = []
    this.logContent = lines
      .map(line => {
        const match = line.match(timestampRegex)
        const timestamp = match && new Date(match[1]!)

        let ts = ''
        if (match && timestamp && !isNaN(Number(timestamp))) {
          ts = timestamp.toUTCString()
          line = line.substring(match[0].length)
        }

        this.timestamps.push(ts)

        return line
      })
      .join('\n')

    this.lineMetaData = pipelinesLogParser.parseLines(this.logContent)
  }

  setData(data: string) {
    this.rawLogData = data
    this.updateLogLines()
  }

  appendData(data: string) {
    // TODO: we should have incremental parsing here
    this.rawLogData += data
    this.updateLogLines()
  }

  appendLines(lines: string[], startLine?: number | null) {
    const offset = this.logLines.length
    const currentTime = new Date().toISOString().replace('Z', '0000Z')

    const data = lines.map(line => `${currentTime} ${line}`).join('\n')
    this.appendData(`${offset ? '\n' : ''}${data}`)

    if (startLine == null) return

    for (let i = 0; i < lines.length; i++) {
      this.logLineNumbers[offset + i] = startLine + i
    }
  }

  updateLogLines() {
    this.updateLineMetaData()

    this.logLines = []
    for (const line of this.lineMetaData) {
      this.logLines.push(...line.nodes)
    }
  }

  setSelectedLine(selectedLine: number) {
    if (this.findResult) this.findResult.selectedLine = selectedLine
  }

  search(text: string, selectFirstLine: boolean): number[] {
    const lines = this.findLines(text)
    const selectedLine = selectFirstLine && lines.length > 0 ? lines[0]!.line : -1
    this.findResult = {text, lines, selectedLine}
    return lines.map(line => line.line)
  }

  findLines(text: string): ISectionFind[] {
    const normalizedText = getText(text)
    const lines: ISectionFind[] = []
    for (const node of this.logLines) {
      const nodeText = this.getNodeTextForSearch(node)
      if (getText(nodeText).indexOf(normalizedText) >= 0) {
        lines.push({line: node.lineIndex})
        if (lines.length >= MAX_LINE_MATCHES_TO_PARSE) {
          return lines
        }
      }
    }
    return lines
  }

  cancelSearch() {
    this.findResult = undefined
    this.updateLogLines()
  }

  renderTemplate(node: ILogLine, groupExpanded: boolean, isCheckRun: boolean): TemplateResult {
    const {lineIndex} = node
    const logLineNumber = this.logLineNumbers[lineIndex]
    const lineNumber = logLineNumber != null ? logLineNumber : lineIndex + 1
    const text = this.getNodeText(node)

    // parse() does the ANSI parsing and converts to HTML
    const lineContent = pipelinesLogParser.parse(text, lineIndex, this.findResult)

    // The parser assigns a type to each line. Such as "debug" or "command".
    // These change the way the line looks. See checks.scss for the css.
    const className = `log-line-${getType(node)}`

    const data: LogLineTemplateData = {
      className,
      lineNumber,
      lineContent,
      timeStamp: this.timestamps[lineIndex],
      lineUrl: `${this.permalinkPrefix}:${lineNumber}`,
      isGroup: node.type === NodeType.Group,
      inGroup: node.group?.lineIndex != null,
      isError: node.type === NodeType.Error,
      isWarning: node.type === NodeType.Warning,
      isNotice: node.type === NodeType.Notice,
      groupExpanded,
    }

    /**
     * Adjust the line number that gets displayed on the left column depending on the number of groups
     *
     * Groups end using a special line that looks like: ##[endgroup] (you can see if if viewing raw logs) however in UI UI this is not visible and is intentionally hidden.
     * The special line is not rendered because lineIndex from parser.ts subtracts 1 from the legnth of the node in this special case (see the parseLines function)
     * An extra "hidden" line however results in each group causing a numeric mispatch and line numbers can go from something like 5 -> 7 if a group is expanded
     *
     * This final line numbers are adjusted here to account for any number of groups in the logs. The more groups there are (groupCount), the larger the compensation that needs to be maintained
     * The primary purpose of this adjustment is because annotations have line numbers for links that originate from the runnner and they match up with the raw logs.
     */
    data.lineNumber = data.lineNumber - node.groupCount

    if (isCheckRun) {
      return checkRunLogLineTemplate(data)
    } else {
      return stepLogLineTemplate(data)
    }
  }

  getNodeText(node: ILogLine) {
    if (node.text == null) {
      node.text = this.logContent.substring(node.start, node.end + 1)
    }
    return node.text
  }

  /**
   * The raw node text can contain ANSI escape codes which are not visible to users in the logs.
   * This function strips them out so that they do not cause search results to be missed
   */
  getNodeTextForSearch(node: ILogLine) {
    node.text = this.getNodeText(node)

    // Global regex pattern must be supplied if using String.replaceAll so `g` is being added to the end of the regex expression
    const regexString = new RegExp(_ansiEscapeCodeRegex, 'g')
    return node.text.replaceAll(regexString, '')
  }

  indexFromNumber(num: number) {
    const index = this.logLineNumbers.indexOf(num)
    if (index < 0) return num - 1
    return index
  }
}

export {stepLogLineTemplate}
